import { getCsvDownloadUrl } from '@modules/api';
import { userMessage } from '@modules/api-error';
import { logError } from '@modules/logging';
import Download from '@mui/icons-material/Download';
import { Alert, Box, Button, Collapse } from '@mui/material';
import { memo, useState } from 'react';
import { toast } from 'react-toastify';

const DOWNLOAD_FAILED = 'We could not get the results file. Please try again.';

async function downloadCsv(groupId: string, interviewId: string): Promise<void> {
    const url = await getCsvDownloadUrl(groupId, interviewId);

    const a = document.createElement('a');
    a.download = 'compiledCsv.csv';
    a.href = url;
    a.click();
    a.remove();
}

interface Props {
    haveResults: boolean;
    groupId: string | undefined;
    interviewId: string | undefined;
}

function DownloadCSvButton({ haveResults, groupId, interviewId }: Props) {
    const [downloaded, setDownloaded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const onDownloadCsv = () => {
        if (!groupId || !interviewId) {
            setErrorMessage('No survey is selected.');
            return;
        }

        if (!haveResults) {
            setErrorMessage('There are no results to download yet.');
            return;
        }

        setDownloaded(true);
        setLoading(true);
        setErrorMessage('');

        return toast
            .promise(downloadCsv(groupId, interviewId), {
                pending: 'Getting csv...',
                // `ApiError` messages are already user-facing — show the real reason.
                error: { render: ({ data }) => userMessage(data, DOWNLOAD_FAILED) },
                success: 'Csv downloaded!',
            })
            .catch((error: unknown) => {
                logError(error instanceof Error ? error : new Error(String(error)));
                setErrorMessage(userMessage(error, DOWNLOAD_FAILED));
            })
            .finally(() => setLoading(false));
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
                loading={loading}
                variant="outlined"
                color="secondary"
                onClick={onDownloadCsv}
                startIcon={<Download />}
            >
                Download .CSV
            </Button>
            <Collapse in={!!errorMessage || downloaded}>
                {errorMessage ? (
                    <Alert severity="error" onClose={() => setErrorMessage('')}>
                        Error Downloading: {errorMessage}
                    </Alert>
                ) : downloaded ? (
                    <Alert severity="info" onClose={() => setDownloaded(false)}>
                        If the file does not download, click again and allow pop-ups on this page.
                    </Alert>
                ) : null}
            </Collapse>
        </Box>
    );
}

export default memo(DownloadCSvButton);
